/**
 * KGGraphViz — Knowledge graph visualization using Cytoscape.js + fCoSE.
 *
 * fCoSE computes layout once, then stops. No continuous simulation.
 * Hover is pure style changes, zero physics. No jitter.
 *
 * Node shapes by type, CSS-like styling, smooth zoom/pan/drag.
 */

import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import CytoscapeComponent from 'react-cytoscapejs';
import cytoscape from 'cytoscape';
import fcose from 'cytoscape-fcose';

// Register fCoSE layout
cytoscape.use(fcose);

// ─── Node type config ───
const TYPE_STYLES = {
  paper:         { shape: 'round-rectangle', color: '#16657d', width: 22, height: 15, label: 'Papers' },
  method:        { shape: 'ellipse',         color: '#2563eb', width: 14, height: 14, label: 'Methods' },
  technique:     { shape: 'ellipse',         color: '#7c3aed', width: 10, height: 10, label: 'Techniques' },
  contribution:  { shape: 'diamond',         color: '#63b3ed', width: 8,  height: 8,  label: 'Contributions' },
  comparison:    { shape: 'diamond',         color: '#c2410c', width: 8,  height: 8,  label: 'Comparisons' },
  limitation:    { shape: 'diamond',         color: '#b91c1c', width: 8,  height: 8,  label: 'Limitations' },
  problem:       { shape: 'diamond',         color: '#ca8a04', width: 8,  height: 8,  label: 'Problems' },
  claim:         { shape: 'diamond',         color: '#8691a0', width: 8,  height: 8,  label: 'Claims' },
  hardware:      { shape: 'ellipse',         color: '#16794e', width: 10, height: 10, label: 'Hardware' },
  attribute:     { shape: 'round-rectangle', color: '#475569', width: 10, height: 8,  label: 'Attributes' },
  figure:        { shape: 'round-rectangle', color: '#d97706', width: 8,  height: 6,  label: 'Figures' },
  table:         { shape: 'round-rectangle', color: '#0891b2', width: 8,  height: 6,  label: 'Tables' },
  impl_language: { shape: 'ellipse',         color: '#6366f1', width: 8,  height: 8,  label: 'Languages' },
  author:        { shape: 'ellipse',         color: '#be185d', width: 8,  height: 8,  label: 'Authors' },
  institution:   { shape: 'round-rectangle', color: '#0369a1', width: 12, height: 8,  label: 'Institutions' },
  reference:     { shape: 'round-rectangle', color: '#94a3b8', width: 8,  height: 6,  label: 'External refs' },
  equation:      { shape: 'diamond',         color: '#db2777', width: 8,  height: 8,  label: 'Equations' },
  dataset:       { shape: 'round-rectangle', color: '#0d9488', width: 12, height: 8,  label: 'Datasets' },
};

const SUBTYPE_COLORS = {
  backbone: '#5b21b6', loss: '#c026d3', dataset: '#0d9488', hardware: '#15803d',
};

const EDGE_COLORS = {
  cites: '#16657d', outperforms: '#d95a3e',
  uses_backbone: '#7c3aed', uses_loss: '#c026d3', trained_on: '#0d9488',
  uses_technique: '#7c3aed', uses_hardware: '#16794e',
  described_in: '#2563eb', contributes: '#63b3ed',
  has_limitation: '#b91c1c', compares: '#c2410c', addresses_problem: '#ca8a04',
  implements_step: '#a0aec0',
  has_figure: '#d97706', has_table: '#0891b2',
  implemented_in: '#6366f1', maintained_by: '#be185d',
  authored_by: '#be185d', affiliated_with: '#0369a1', published_from: '#0369a1',
  cites_external: '#94a3b8', has_equation: '#db2777',
  // Derived TEI relationships
  co_authored_with: '#ec4899', colleagues_with: '#0ea5e9',
  co_cited_with: '#64748b', shares_bibliography: '#94a3b8',
  author_works_on: '#a78bfa', uses_dataset: '#0d9488',
};

// ─── Cytoscape stylesheet ───
const STYLESHEET = [
  // Default node
  { selector: 'node', style: {
    'label': 'data(label)',
    'font-family': '"PT Sans", sans-serif',
    'font-size': '10px',
    'font-weight': 600,
    'text-valign': 'bottom',
    'text-halign': 'center',
    'text-margin-y': 4,
    'color': '#2a3142',
    'text-max-width': '80px',
    'text-wrap': 'ellipsis',
    'background-color': 'data(color)',
    'shape': 'data(shape)',
    'width': 'data(width)',
    'height': 'data(height)',
    'border-width': 1,
    'border-color': '#00000018',
    'transition-property': 'opacity, border-width, border-color',
    'transition-duration': '0.15s',
  }},
  // Sized by degree for techniques
  { selector: 'node[type="technique"]', style: {
    'width': 'mapData(degree, 0, 20, 12, 32)',
    'height': 'mapData(degree, 0, 20, 12, 32)',
  }},
  // Paper nodes slightly larger
  { selector: 'node[type="paper"]', style: {
    'width': 24, 'height': 16,
    'font-size': '9px',
  }},
  // Method nodes
  { selector: 'node[type="method"]', style: {
    'width': 16, 'height': 16,
    'font-size': '9px',
  }},
  // Default edge
  { selector: 'edge', style: {
    'width': 1,
    'line-color': '#d0d5dd',
    'curve-style': 'bezier',
    'opacity': 0.35,
    'transition-property': 'opacity, width, line-color',
    'transition-duration': '0.15s',
  }},
  // Typed edges
  ...Object.entries(EDGE_COLORS).map(([type, color]) => ({
    selector: `edge[type="${type}"]`,
    style: { 'line-color': color },
  })),
  // Citation edges slightly thicker
  { selector: 'edge[type="cites"]', style: { 'width': 1.5, 'opacity': 0.5 } },
  { selector: 'edge[type="outperforms"]', style: { 'width': 2, 'opacity': 0.6, 'line-style': 'dashed' } },
  // Hover highlight
  { selector: 'node.highlighted', style: {
    'border-width': 2.5,
    'border-color': '#16657d',
    'z-index': 10,
    'font-size': '11px',
    'text-background-color': '#ffffff',
    'text-background-opacity': 0.85,
    'text-background-padding': '2px',
  }},
  { selector: 'node.neighbor', style: {
    'opacity': 1,
    'border-width': 1.5,
    'border-color': '#16657d55',
  }},
  { selector: 'node.dimmed', style: { 'opacity': 0.08 } },
  { selector: 'edge.highlighted', style: { 'opacity': 0.7, 'width': 2 } },
  { selector: 'edge.dimmed', style: { 'opacity': 0.02 } },
  // Selected
  { selector: 'node.selected', style: {
    'border-width': 3,
    'border-color': '#d95a3e',
  }},
  // Hidden
  { selector: 'node.hidden', style: { 'display': 'none' } },
  { selector: 'edge.hidden', style: { 'display': 'none' } },
  { selector: 'node.hidden-low-degree', style: { 'display': 'none' } },
  { selector: 'edge.hidden-low-degree', style: { 'display': 'none' } },
  // External dim/highlight
  { selector: 'node.ext-dim', style: { 'opacity': 0.12 } },
  { selector: 'node.ext-hl', style: { 'opacity': 1, 'border-width': 2, 'border-color': '#1a202c' } },
  { selector: 'edge.ext-dim', style: { 'opacity': 0.06 } },
  { selector: 'edge.ext-hl', style: { 'opacity': 0.9, 'width': 2 } },
  { selector: 'node.ext-hl', style: { 'border-width': 3, 'border-color': '#d95a3e' } },
  // Search
  { selector: 'node.search-match', style: { 'border-width': 3, 'border-color': '#eab308', 'z-index': 999 } },
  { selector: '.search-dim', style: { 'opacity': 0.06 } },
];

// ─── fCoSE layout config ───
// The edge-weighted variants below pull paper↔method (described_in) and
// paper↔author/institution tight, while letting noisy high-degree types
// (references, citations) relax. This keeps a paper visually adjacent to
// its own method instead of clustering papers and methods separately.
const SHORT_EDGE_TYPES = new Set([
  'described_in',       // paper ↔ its method — most important
  'authored_by',        // paper ↔ author
  'published_from',     // paper ↔ institution
  'has_table', 'has_figure', 'has_equation',  // paper ↔ its own content
]);
const LONG_EDGE_TYPES = new Set([
  'cites_external',     // don't let external refs drag layout
  'similar_in_role', 'semantically_similar',
]);

const LAYOUT = {
  name: 'fcose',
  quality: 'default',
  randomize: true,
  animate: true,
  animationDuration: 700,
  animationEasing: 'ease-out',
  nodeRepulsion: 25000,
  idealEdgeLength: edge => {
    const t = edge.data('type');
    if (SHORT_EDGE_TYPES.has(t)) return 40;   // tight
    if (LONG_EDGE_TYPES.has(t)) return 260;   // loose
    return 140;                                // default
  },
  edgeElasticity: edge => {
    const t = edge.data('type');
    if (SHORT_EDGE_TYPES.has(t)) return 0.7;  // stiff → enforce closeness
    if (LONG_EDGE_TYPES.has(t)) return 0.05;  // floppy
    return 0.2;
  },
  nestingFactor: 0.1,
  gravity: 0.06,
  gravityRange: 7.0,
  numIter: 4000,
  tile: true,
  fit: true,
  padding: 15,
};

// The landing macro graph is zoomed one step PAST fit-to-all so it reads as a
// graph rather than a distant dust cloud (per PI request). Focused subgraphs
// (few nodes) and the explicit "Fit" button stay at plain fit-to-all.
const MACRO_ZOOM_IN = 1.4;
function fitMacroView(cy) {
  if (!cy) return;
  cy.fit(undefined, 10);
  if (cy.nodes().length > 40) {
    cy.zoom({ level: cy.zoom() * MACRO_ZOOM_IN, renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 } });
  }
}

export default function KGGraphViz({
  onNodeClick, selectedNode, height = 420, dataUrl, postData,
  onNodeSelect, onEdgeSelect, onNodeHover, onBackgroundTap, refitTrigger,
  hiddenEdgeTypes, hiddenNodeTypes: extHiddenNodeTypes, highlightedLabels, dimUnhighlighted,
  focusOnHighlight = false,
  minDegree = 0, searchTerm = '', viewName, hideTooltip = false,
}) {
  const cyRef = useRef(null);
  const containerRef = useRef(null);
  const [graphData, setGraphData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Default-hidden node types: noisy detail-level types that flood the landing view.
  // Users can click the legend to toggle any of them back on.
  const [hiddenTypes, setHiddenTypes] = useState(
    () => new Set(['figure', 'table', 'author', 'reference', 'equation', 'contribution', 'comparison', 'limitation', 'problem'])
  );
  const [tooltipNode, setTooltipNode] = useState(null);
  const [tooltipEdge, setTooltipEdge] = useState(null);
  const tooltipTimer = useRef(null);
  // Cooperative-gesture hint state. Briefly visible when a user scrolls
  // over the graph without the modifier — so they learn the requirement
  // instead of thinking the page is just unresponsive.
  const [zoomHint, setZoomHint] = useState(false);
  const [legendOpen, setLegendOpen] = useState(false);
  const zoomHintTimer = useRef(null);
  // True while a node is being hovered. The external (label-based) highlight
  // effect skips while this is set, so a hover shows ONLY the node's own
  // neighborhood — the table-bridge no longer re-dims the graph to the
  // method's smaller semantic cohort (which looked like an auto-click).
  const hoverActiveRef = useRef(false);

  // Load graph data from static JSON. When `postData` carries paperIds we
  // mirror the legacy /api/kg-subgraph behavior: load kg-full, build the
  // graphology graph, and run extractSubgraph(paperIds, intent) client-side
  // so the post-query view shows a focused subgraph instead of all 3,549 nodes.
  // Re-runs whenever the paperIds payload changes (stringified for stable dep).
  const postKey = postData ? JSON.stringify(postData) : '';
  useEffect(() => {
    setLoading(true);
    let cancelled = false;
    const wantsSubgraph = !!(
      postData && Array.isArray(postData.paperIds) && postData.paperIds.length > 0
    );

    const finish = (data) => {
      if (cancelled) return;
      if (data && (data.nodes || data.success || Array.isArray(data))) {
        setGraphData(data.success ? data : {
          success: true,
          ...(Array.isArray(data) ? { nodes: [], links: data } : data),
        });
      }
      setLoading(false);
    };

    let loader;
    if (wantsSubgraph || dataUrl === 'kg-full' || (dataUrl || '').includes('kg-subgraph')) {
      // Load full KG, then either render whole-thing or extract focused subgraph.
      loader = Promise.all([
        import('../lib/data-loader').then(m => m.loadKgFull()),
        import('../lib/kg-graph'),
      ]).then(([full, kgLib]) => {
        if (!wantsSubgraph) return full;
        kgLib.initGraph(full);
        const sub = kgLib.extractSubgraph(postData.paperIds, postData.intent || 'general');
        return { success: true, nodes: sub.nodes, links: sub.links, stats: sub.stats };
      });
    } else {
      loader = import('../lib/data-loader').then(m => m.loadKgMacro());
    }
    loader.then(finish).catch(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUrl, postKey]);

  // Build Cytoscape elements. Node types in `hiddenTypes` are dropped up-front so
  // the initial fCoSE layout only positions visible nodes — critical for a clean
  // landing view when the graph has thousands of detail-level nodes (refs,
  // equations, figures, tables, authors).
  const elements = useMemo(() => {
    if (!graphData) return [];
    const visibleNodes = graphData.nodes.filter(n => !hiddenTypes.has(n.type));
    const visibleIds = new Set(visibleNodes.map(n => n.id));
    const nodes = visibleNodes.map(n => {
      const cfg = TYPE_STYLES[n.type] || TYPE_STYLES.claim;
      const color = SUBTYPE_COLORS[n.subtype] || cfg.color;
      return {
        data: {
          id: n.id,
          label: n.label || '',
          type: n.type,
          subtype: n.subtype || '',
          color: color,
          shape: cfg.shape,
          width: cfg.width,
          height: cfg.height,
          degree: n.degree || 0,
          value: n.value || '',
          paper_id: n.paper_id || '',
        },
      };
    });
    const edges = graphData.links
      .filter(e => {
        const srcId = e.source?.id || e.source;
        const tgtId = e.target?.id || e.target;
        return visibleIds.has(srcId) && visibleIds.has(tgtId);
      })
      .map((e, i) => ({
        data: {
          id: `e${i}`,
          source: e.source?.id || e.source,
          target: e.target?.id || e.target,
          type: e.type || '',
          sentiment: e.sentiment || '',
          contexts: e.contexts || [],
          mentions: e.mentions || 0,
        },
      }));
    // Remove orphan nodes (no edges after visibility filtering)
    const connectedIds = new Set();
    edges.forEach(e => { connectedIds.add(e.data.source); connectedIds.add(e.data.target); });
    const filteredNodes = nodes.filter(n => connectedIds.has(n.data.id));
    return [...filteredNodes, ...edges];
  }, [graphData, hiddenTypes]);

  // Setup events after Cytoscape mounts
  const handleCy = useCallback(cy => {
    if (!cy || cyRef.current === cy) return;
    cyRef.current = cy;
    // Expose to window in dev so the Playwright UI tests can reach in
    // and tap nodes by label without depending on canvas pixel coords.
    // Wrapped in a localhost check so this never leaks to production.
    if (typeof window !== 'undefined' && window.location && window.location.hostname === 'localhost') {
      window.__kgCy = cy;
    }

    cy.userZoomingEnabled(false);
    // Zoom the initial landing view in a step past fit-to-all once the first
    // fCoSE layout settles.
    cy.one('layoutstop', () => fitMacroView(cy));

    // NOTE: the cooperative wheel-to-zoom handler is attached in a dedicated
    // mount effect (see below), NOT here — the cy-callback fires at a timing that
    // differs between the dev server and a production build, which could leave the
    // listener unbound in prod (the "no zoom hint in production" bug).

    // Track which node is tap-selected so the hover mouseout doesn't
    // clear the persistent neighborhood highlight.
    let pinnedNode = null;
    // Hover is debounced: highlights/tooltips only commit once the cursor SETTLES
    // for this long. In a dense graph a tiny movement otherwise flips between
    // adjacent nodes/edges and the highlights strobe.
    const HOVER_DELAY = 140;

    const applyNeighborhoodHighlight = (node) => {
      const neighborhood = node.closedNeighborhood();
      cy.elements().not(neighborhood).addClass('dimmed');
      neighborhood.nodes().addClass('neighbor');
      node.addClass('highlighted').removeClass('neighbor');
      neighborhood.edges().addClass('highlighted');
    };

    const clearNeighborhoodHighlight = () => {
      cy.elements().removeClass('dimmed highlighted neighbor');
    };

    // Hover: highlight neighborhood once the cursor settles (debounced). The
    // neighborhood highlight, the table bridge, and the tooltip all fire together
    // on the same delayed timer, so a quick pass-through commits nothing.
    cy.on('mouseover', 'node', e => {
      const node = e.target;
      clearTimeout(tooltipTimer.current);
      tooltipTimer.current = setTimeout(() => {
        hoverActiveRef.current = true;   // suppress the label-based graph re-dim
        clearNeighborhoodHighlight();
        applyNeighborhoodHighlight(node);
        // Bridge the hover up so the parent can spotlight the matching row in
        // the method table above (the reverse of table-row -> node, which the
        // landing's highlight resolver already does via hoveredIndex).
        if (onNodeHover) onNodeHover(node.data('label'), node.data('type'), node.data('id'));
        setTooltipNode({ label: node.data('label'), type: node.data('type'), subtype: node.data('subtype'), degree: node.data('degree'), value: node.data('value') });
      }, HOVER_DELAY);
    });

    cy.on('mouseout', 'node', () => {
      clearTimeout(tooltipTimer.current);   // cancel a pending (passed-through) hover
      hoverActiveRef.current = false;
      if (pinnedNode) {
        clearNeighborhoodHighlight();
        applyNeighborhoodHighlight(pinnedNode);
      } else {
        clearNeighborhoodHighlight();
      }
      if (onNodeHover) onNodeHover(null);
      setTooltipNode(null);
    });

    // Hover an edge — surface a short orientation tooltip. Per the
    // no-redundancy rule, this stays orientation-only; clicking opens the
    // full detail in the side panel.
    cy.on('mouseover', 'edge', e => {
      const edge = e.target;
      const srcNode = edge.source();
      const tgtNode = edge.target();
      clearTimeout(tooltipTimer.current);
      tooltipTimer.current = setTimeout(() => {
        setTooltipEdge({
          src: srcNode.data('label'),
          tgt: tgtNode.data('label'),
          src_type: srcNode.data('type'),
          tgt_type: tgtNode.data('type'),
          edge_type: edge.data('type'),
          sentiment: edge.data('sentiment') || '',
        });
      }, HOVER_DELAY);
    });

    cy.on('mouseout', 'edge', () => {
      clearTimeout(tooltipTimer.current);
      setTooltipEdge(null);
    });

    // Tap an edge — fire onEdgeSelect with the full edge payload so the
    // parent can render a focused comparison side panel. Tapping an edge
    // also clears any prior node selection in the parent (mutual
    // exclusivity is enforced there).
    cy.on('tap', 'edge', e => {
      const edge = e.target;
      const srcNode = edge.source();
      const tgtNode = edge.target();
      if (onEdgeSelect) {
        onEdgeSelect({
          edge: {
            type: edge.data('type'),
            sentiment: edge.data('sentiment') || '',
            contexts: edge.data('contexts') || [],
          },
          src: {
            id: srcNode.data('id'), label: srcNode.data('label'),
            type: srcNode.data('type'), paper_id: srcNode.data('paper_id'),
          },
          tgt: {
            id: tgtNode.data('id'), label: tgtNode.data('label'),
            type: tgtNode.data('type'), paper_id: tgtNode.data('paper_id'),
          },
        });
      }
    });

    // Tap a node to select it; tap the SAME (already-selected) node again to
    // deselect — a consistent toggle. Selecting pins the neighborhood highlight
    // and emits to the parent; deselecting routes through the same full-clear as
    // a background tap so no selection state is left dangling.
    cy.on('tap', 'node', e => {
      const node = e.target;
      const wasSelected = node.hasClass('selected');   // robust toggle detection
      cy.nodes().removeClass('selected');
      clearNeighborhoodHighlight();
      if (wasSelected) {
        pinnedNode = null;
        hoverActiveRef.current = false;
        if (onBackgroundTap) onBackgroundTap();
        else if (onNodeSelect) onNodeSelect(null);
        return;
      }
      pinnedNode = node;
      node.addClass('selected');
      applyNeighborhoodHighlight(node);
      if (onNodeClick) onNodeClick({ id: node.data('id'), label: node.data('label'), type: node.data('type'), subtype: node.data('subtype') });
      if (onNodeSelect) {
        const neighbors = node.neighborhood().nodes().map(n => ({ ...n.data() }));
        const edges = node.connectedEdges().map(ed => ({
          ...ed.data(),
          source: ed.data('source'), target: ed.data('target'),
        }));
        onNodeSelect({ node: { ...node.data() }, neighbors, edges, viewName });
      }
    });

    // Tap empty canvas -> FULL deselect (node + edge + every sticky highlight),
    // not just the side panel. This is the standard escape hatch.
    cy.on('tap', e => {
      if (e.target === cy) {
        pinnedNode = null;
        hoverActiveRef.current = false;
        clearNeighborhoodHighlight();
        cy.nodes().removeClass('selected');
        if (onBackgroundTap) onBackgroundTap();
        else if (onNodeSelect) onNodeSelect(null);
      }
    });
  }, [onNodeClick, onNodeSelect, onEdgeSelect, onNodeHover, onBackgroundTap, viewName]);

  // Re-fit on trigger change (also resize so Cytoscape recalculates its
  // viewport from the possibly-resized container, e.g. when the side
  // panel opens/closes).
  useEffect(() => {
    if (cyRef.current) {
      setTimeout(() => {
        cyRef.current?.resize();
        fitMacroView(cyRef.current);
      }, 250);
    }
  }, [refitTrigger]);

  // Auto-resize + fit when the container dimensions change (e.g. CSS
  // grid transition from side panel opening/closing). Depends on `loading` for
  // the same reason as the wheel effect: the container only exists post-load.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const cy = cyRef.current;
      if (cy) { cy.resize(); fitMacroView(cy); }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cooperative wheel-to-zoom + the "⌘/Ctrl + scroll to zoom" hint. Depends on
  // `loading` because the .kgv-container only renders AFTER loading clears — a
  // mount-only ([]) effect ran while the loading placeholder was showing (ref
  // null), bailed, and never bound. Re-running when loading flips to false
  // attaches the listener to the real container (and rebinds after a re-query).
  useEffect(() => {
    const wrapper = containerRef.current;
    if (!wrapper) return;
    const onWheel = (ev) => {
      const cy = cyRef.current;
      if (ev.metaKey || ev.ctrlKey) {
        ev.preventDefault();
        if (!cy) return;
        const factor = ev.deltaY < 0 ? 1.08 : 0.92;
        const rect = wrapper.getBoundingClientRect();
        cy.zoom({ level: cy.zoom() * factor, renderedPosition: { x: ev.clientX - rect.left, y: ev.clientY - rect.top } });
      } else {
        setZoomHint(true);
        clearTimeout(zoomHintTimer.current);
        zoomHintTimer.current = setTimeout(() => setZoomHint(false), 1200);
      }
    };
    wrapper.addEventListener('wheel', onWheel, { passive: false });
    return () => wrapper.removeEventListener('wheel', onWheel);
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Toggle type visibility
  const toggleType = useCallback(type => {
    setHiddenTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  // Hidden types now filter elements up-front. When toggling re-adds a type,
  // re-run the layout so new nodes get positioned instead of stacking at origin.
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy || !graphData) return;
    cy.layout(LAYOUT).run();
  }, [hiddenTypes, graphData]);

  // Apply external edge-type filters
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    const hidden = hiddenEdgeTypes instanceof Set ? hiddenEdgeTypes : new Set(hiddenEdgeTypes || []);
    cy.edges().forEach(e => {
      if (hidden.has(e.data('type'))) e.addClass('hidden');
      else e.removeClass('hidden');
    });
  }, [hiddenEdgeTypes, elements]);

  // External node-type filters (CSS-hide only; for transient UI toggles). Local
  // legend-driven `hiddenTypes` already filters elements up-front.
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    const hidden = extHiddenNodeTypes instanceof Set ? extHiddenNodeTypes : new Set(extHiddenNodeTypes || []);
    cy.nodes().forEach(n => {
      if (hidden.has(n.data('type'))) {
        n.addClass('hidden');
        n.connectedEdges().addClass('hidden');
      } else {
        n.removeClass('hidden');
      }
    });
  }, [extHiddenNodeTypes, elements]);

  // Minimum-degree filter: hide nodes with fewer neighbors than threshold
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    cy.nodes().forEach(n => {
      const degree = n.data('degree') || 0;
      if (minDegree > 0 && degree < minDegree) {
        n.addClass('hidden-low-degree');
        n.connectedEdges().addClass('hidden-low-degree');
      } else {
        n.removeClass('hidden-low-degree');
        n.connectedEdges().removeClass('hidden-low-degree');
      }
    });
  }, [minDegree, elements]);

  // Search: focus on matching node + its neighborhood, dim everything else
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    cy.elements().removeClass('search-match search-dim');
    const term = (searchTerm || '').trim().toLowerCase();
    if (!term) return;
    const matches = cy.nodes().filter(n => (n.data('label') || '').toLowerCase().includes(term));
    if (matches.length === 0) return;
    const keep = matches.closedNeighborhood();
    cy.elements().not(keep).addClass('search-dim');
    matches.addClass('search-match');
  }, [searchTerm, elements]);

  // Apply external highlighting across ALL node types so a sidebar click
  // (institution, author, technique, year, foundational work) lights up
  // the graph's connected cohort — papers, methods, entity itself —
  // and dims everything else. This is the "everything connects" pass.
  // Skip when a graph node is selected — the tap handler's neighborhood
  // highlight (dimmed/highlighted/neighbor classes) should be the sole
  // visual so the user sees every topological neighbor, not just the
  // cross-widget label matches.
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    cy.nodes().removeClass('ext-dim ext-hl');
    cy.edges().removeClass('ext-dim ext-hl');
    // Skip while a graph node is selected OR hovered — its own neighborhood
    // highlight is the sole visual; don't layer the label-cohort dim on top.
    if (selectedNode || hoverActiveRef.current) return;
    const labels = highlightedLabels instanceof Set ? highlightedLabels : new Set(highlightedLabels || []);
    if (labels.size === 0 || !dimUnhighlighted) return;
    const normalized = new Set([...labels].map(l => (l || '').toLowerCase()));
    const hlNodes = cy.collection();
    cy.nodes().forEach(n => {
      const lbl = (n.data('label') || '').toLowerCase();
      if (normalized.has(lbl)) {
        n.addClass('ext-hl');
        hlNodes.merge(n);
      } else {
        n.addClass('ext-dim');
      }
    });
    // Brighten edges between highlighted nodes so the cohort reads as a cluster
    cy.edges().forEach(e => {
      if (hlNodes.contains(e.source()) && hlNodes.contains(e.target())) {
        e.addClass('ext-hl');
      } else {
        e.addClass('ext-dim');
      }
    });
    // WHERE-on-the-graph: pan/zoom the viewport to the highlighted cohort so the
    // user actually sees the selection instead of hunting for lit nodes among
    // dimmed ones. Only for a deliberate click-selection (focusOnHighlight), and
    // only when it's a real subset (never for a near-everything highlight).
    if (focusOnHighlight && hlNodes.nonempty() && hlNodes.length < cy.nodes().length) {
      cy.stop();
      cy.animate({ fit: { eles: hlNodes, padding: 80 } }, { duration: 420, easing: 'ease-in-out-cubic' });
    }
  }, [highlightedLabels, dimUnhighlighted, elements, selectedNode, focusOnHighlight]);

  if (loading) return <div className="kgv-loading">Loading graph...</div>;
  if (!elements.length) return <div className="kgv-loading">No graph data</div>;

  // Count types from original graphData (not filtered elements) so the legend
  // keeps showing the total count for hidden types, signaling they're available.
  const typeCounts = {};
  if (graphData) {
    graphData.nodes.forEach(n => {
      if (n.type) typeCounts[n.type] = (typeCounts[n.type] || 0) + 1;
    });
  }

  return (
    <div ref={containerRef} className="kgv-container" style={{ position: 'relative' }}>
      {/* Legend — collapsible overlay */}
      <div className={`kgv-legend ${legendOpen ? 'open' : ''}`}>
        <button className="kgv-legend-toggle" onClick={() => setLegendOpen(o => !o)}>
          Legend
          <span className="kgv-legend-chevron">{legendOpen ? '▾' : '▸'}</span>
        </button>
        <button className="kgv-fit-btn" onClick={() => cyRef.current?.fit(undefined, 10)}>Fit</button>

        {legendOpen && (
          <div className="kgv-legend-body">
            <div className="kgv-legend-col">
              <span className="kgv-legend-heading">Nodes</span>
              <div className="kgv-legend-grid">
                {Object.entries(TYPE_STYLES).map(([type, cfg]) => {
                  const count = typeCounts[type] || 0;
                  if (count === 0) return null;
                  const isHidden = hiddenTypes.has(type);
                  return (
                    <button key={type} className={`kgv-legend-btn ${isHidden ? 'off' : ''}`} onClick={() => toggleType(type)}>
                      <span className="kgv-legend-shape" style={{
                        background: isHidden ? 'var(--border)' : cfg.color,
                        borderRadius: cfg.shape === 'round-rectangle' ? '2px' : cfg.shape === 'diamond' ? '1px' : '50%',
                        transform: cfg.shape === 'diamond' ? 'rotate(45deg) scale(0.75)' : 'none',
                      }} />
                      {cfg.label}
                      <span className="kgv-legend-count">{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="kgv-legend-col">
              <span className="kgv-legend-heading">Edges</span>
              <div className="kgv-legend-grid">
                {[
                  { color: EDGE_COLORS.cites,             label: 'Cites',         dashed: false },
                  { color: EDGE_COLORS.outperforms,       label: 'Outperforms',   dashed: true },
                  { color: EDGE_COLORS.uses_backbone,     label: 'Uses technique', dashed: false },
                  { color: EDGE_COLORS.contributes,       label: 'Contributes',   dashed: false },
                  { color: EDGE_COLORS.has_limitation,    label: 'Limitation',    dashed: false },
                  { color: EDGE_COLORS.addresses_problem, label: 'Addresses',     dashed: false },
                  { color: EDGE_COLORS.compares,          label: 'Compares',      dashed: false },
                  { color: EDGE_COLORS.authored_by,       label: 'Authored by',   dashed: false },
                  { color: EDGE_COLORS.described_in,      label: 'Described in',  dashed: false },
                ].map(({ color, label, dashed }) => (
                  <span key={label} className="kgv-legend-btn static">
                    <span className={`kgv-legend-line ${dashed ? 'dashed' : 'solid'}`} style={{ borderTopColor: color }} />
                    {label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Cytoscape graph */}
      <CytoscapeComponent
        elements={elements}
        stylesheet={STYLESHEET}
        layout={LAYOUT}
        style={{ width: '100%', height }}
        cy={handleCy}
      />

      {/* Cooperative-gesture hint (Mapbox pattern). Surfaces only when a
          user wheels over the graph without the modifier; teaches the
          requirement without permanently cluttering the chrome. */}
      {zoomHint && (
        <div className="kgv-coop-hint" role="status" aria-live="polite">
          Use {navigator.platform.toLowerCase().includes('mac') ? '⌘' : 'Ctrl'} + scroll to zoom
        </div>
      )}

      {/* Tooltip — suppressed while a pinned info box is open (it shares this
          bottom-left corner; showing both stacks two boxes). */}
      {!hideTooltip && tooltipNode && (
        <div className="kgv-tooltip">
          <div className="kgv-tooltip-type" style={{ color: TYPE_STYLES[tooltipNode.type]?.color }}>
            {tooltipNode.subtype || tooltipNode.type}
          </div>
          <div className="kgv-tooltip-label">{tooltipNode.label}</div>
          {tooltipNode.degree > 0 && <div className="kgv-tooltip-degree">{tooltipNode.degree} connections</div>}
        </div>
      )}

      {/* Edge tooltip — orientation only (≤4 lines). Click opens the full
          comparison side panel; we do not duplicate that detail here. */}
      {!hideTooltip && tooltipEdge && !tooltipNode && (
        <div className="kgv-tooltip kgv-tooltip-edge">
          <div className="kgv-tooltip-edge-pair">
            <span>{tooltipEdge.src}</span>
            <span className="kgv-tooltip-edge-arrow">→</span>
            <span>{tooltipEdge.tgt}</span>
          </div>
          <div className="kgv-tooltip-edge-type">
            {tooltipEdge.edge_type}
            {tooltipEdge.sentiment && (
              <span className={`kgv-tooltip-stance stance-${tooltipEdge.sentiment}`}>
                {tooltipEdge.sentiment === 'builds_on' ? 'builds on'
                  : tooltipEdge.sentiment === 'differs_from' ? 'differs'
                  : 'neutral'}
              </span>
            )}
          </div>
          <div className="kgv-tooltip-edge-hint">click to compare →</div>
        </div>
      )}
    </div>
  );
}
